import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ConfigProvider } from "@/context/ConfigContext";
import { RequireAuth, RequireConsole, RequireMember } from "@/components/Guards";
import PublicLayout from "@/components/public/SiteLayout";
import SitePage, { ArticlePage } from "@/pages/public/SitePage";
import InvitationRequest from "@/pages/public/InvitationRequest";
import AuthPage from "@/pages/public/AuthPage";
import { Join, ResetCode } from "@/pages/public/Join";
import { Admissions, SponsorGraph } from "@/pages/console/Admissions";
import Candidate from "@/pages/public/Candidate";
import SimulatedCheckout from "@/pages/public/SimulatedCheckout";
import MemberLayout from "@/components/member/MemberLayout";
import MemberHome, { PostPage } from "@/pages/member/Home";
import Announcements, { AnnouncementDetail } from "@/pages/member/Announcements";
import ConsoleAnnouncements from "@/pages/console/Announcements";
import Network from "@/pages/member/Network";
import MemberDetail from "@/pages/member/MemberDetail";
import Operations from "@/pages/member/Operations";
import OperationDetail from "@/pages/member/OperationDetail";
import OperationForm from "@/pages/member/OperationForm";
import { Conversations, JoinGroup, Thread } from "@/pages/member/Messages";
import { Assistant, EventDetail, Events, SupplierDetail, Suppliers } from "@/pages/member/Commerce";
import { ConsoleEvents, ConsoleSuppliers } from "@/pages/console/Commerce";
import { ConsoleTemplates } from "@/components/member/LeadTools";
import { Notifications, Search } from "@/pages/member/Misc";
import Me from "@/pages/member/Me";
import ConsoleLayout from "@/components/console/ConsoleLayout";
import { ConsoleOperations, Finances, Requests } from "@/pages/console/Overview";
import { Dashboard, MessagingAdmin, Moderation } from "@/pages/console/Phase1C";
import { Audit, DevLog, Members, Payments } from "@/pages/console/Records";
import { Docs, SettingsPage } from "@/pages/console/Manage";
import { BlogAdmin, ContentEditor, MediaLibrary } from "@/pages/console/SiteAdmin";
import Engagement from "@/pages/console/Engagement";
import Validations from "@/pages/console/Validations";
import ConsolePartners from "@/pages/console/Partners";
import ConsoleMemberDetail from "@/pages/console/MemberDetail";
import { useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { hostRedirect } from "@/lib/hosts";

function HostGate() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    const to = hostRedirect(pathname);
    if (!to) return;
    if (/^https?:/.test(to)) window.location.replace(to);
    else nav(to, { replace: true });
  }, [pathname, nav]);
  return null;
}

export default function App() {
  return (
    <ConfigProvider>
      <AuthProvider>
        <BrowserRouter>
          <HostGate />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route index element={<SitePage />} />
              <Route path="demande-invitation" element={<InvitationRequest />} />
              <Route path="connexion" element={<Navigate to="/app/connexion" replace />} />
              <Route path="rejoindre" element={<Join />} />
              <Route path="reinitialiser-code" element={<ResetCode />} />
              <Route path="ressources/blog/:slug" element={<ArticlePage />} />
              <Route path="*" element={<SitePage />} />
            </Route>
            <Route path="console/connexion" element={<AuthPage console />} />
            <Route path="app/connexion" element={<AuthPage />} />
            <Route path="candidature" element={<RequireAuth><Candidate /></RequireAuth>} />
            <Route path="paiement/simule/:sid" element={<RequireAuth><SimulatedCheckout /></RequireAuth>} />
            <Route path="app" element={<RequireMember><MemberLayout /></RequireMember>}>
              <Route index element={<MemberHome />} />
              <Route path="fil/:id" element={<PostPage />} />
              <Route path="annonces" element={<Announcements />} />
              <Route path="annonces/:id" element={<AnnouncementDetail />} />
              <Route path="reseau" element={<Network />} />
              <Route path="reseau/:id" element={<MemberDetail />} />
              <Route path="operations" element={<Operations />} />
              <Route path="operations/nouvelle" element={<OperationForm />} />
              <Route path="operations/:id" element={<OperationDetail />} />
              <Route path="messages" element={<Conversations />} />
              <Route path="fournisseurs" element={<Suppliers />} />
              <Route path="fournisseurs/:id" element={<SupplierDetail />} />
              <Route path="evenements" element={<Events />} />
              <Route path="evenements/:id" element={<EventDetail />} />
              <Route path="assistant" element={<Assistant />} />
              <Route path="messages/rejoindre/:token" element={<JoinGroup />} />
              <Route path="messages/:id" element={<Thread />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="recherche" element={<Search />} />
              <Route path="moi" element={<Me />} />
            </Route>
            <Route path="console" element={<RequireConsole><ConsoleLayout /></RequireConsole>}>
              <Route index element={<Dashboard />} />
              <Route path="engagement" element={<Engagement />} />
              <Route path="validations" element={<Validations />} />
              <Route path="partenaires" element={<ConsolePartners />} />
              <Route path="membres/:id" element={<ConsoleMemberDetail />} />
              <Route path="membres" element={<Members />} />
              <Route path="admission" element={<Admissions />} />
              <Route path="parrainage" element={<SponsorGraph />} />
              <Route path="demandes" element={<Requests />} />
              <Route path="cotisations" element={<Payments />} />
              <Route path="operations" element={<ConsoleOperations />} />
              <Route path="finances" element={<Finances />} />
              <Route path="conventions" element={<ConsoleTemplates />} />
              <Route path="evenements" element={<ConsoleEvents />} />
              <Route path="fournisseurs" element={<ConsoleSuppliers />} />
              <Route path="moderation" element={<Moderation />} />
              <Route path="annonces" element={<ConsoleAnnouncements />} />
              <Route path="messagerie" element={<MessagingAdmin />} />
              <Route path="contenu" element={<ContentEditor />} />
              <Route path="mediatheque" element={<MediaLibrary />} />
              <Route path="blog" element={<BlogAdmin />} />
              <Route path="parametres" element={<SettingsPage />} />
              <Route path="audit" element={<Audit />} />
              <Route path="journal-dev" element={<DevLog />} />
              <Route path="documentation" element={<Docs />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </ConfigProvider>
  );
}
